import instance, { ApiResponse } from 'api';
import { endpoints } from 'endpoints.config';

export type Subject = {
  subjectsID: number;
  internalCode: string;
  name: string;
  shortName: string;
};

export type SubjectContents = Record<string, string>;

export type LessonsKeyStage = {
  name: string;
  strippedName: string;
};

export type LessonsActivity = {
  name: string;
  strippedName: string;
  bIsGroup: boolean;
  colorCSS: string | null;
  headerCSS: string | null;
  imagePath: string | null;
};

export type LessonsAccessStatus =
  | 'Full'
  | 'AccountPaused'
  | 'CompleteSubscriptionPayment'
  | 'UpgradePackage';

export type LessonsAccess = {
  bFullAccess: boolean;
  status: LessonsAccessStatus;
  bFreeTrial: boolean;
  trialLessonLimit: number | null;
};

export type LearningObjective = {
  objective: string;
};

export type UnitLesson = {
  lessonsId: number;
  lessonNumber: number;
  theme: string | null;
  bFreeSample: boolean;
  bCanOpen: boolean;
  yearDisplay: string | null;
};

export type Unit = {
  keyStage: LessonsKeyStage;
  keyStageName: string;
  access: LessonsAccess;
  activity: LessonsActivity;
  schemeOfWork: {
    schemeOfWorksId: number;
    name: string;
    bFreeSample: boolean;
    bCanOpen: boolean;
    yearDisplay: string | null;
  } | null;
  knowledgeOrganiser: { resourcesId: number; name: string } | null;
  lessons: UnitLesson[];
};

export type SchemeOfWork = {
  keyStage: LessonsKeyStage;
  keyStageName: string;
  schoolName: string;
  access: LessonsAccess;
  activity: LessonsActivity;
  schemeOfWork: {
    schemeOfWorksId: number;
    introduction: string | null;
    keyVocabulary: string | null;
    healthSafety: string | null;
  };
  nationalCurriculums: {
    nationalCurriculumsId: number;
    nationalCurriculumArea: string;
    learningAreas: { specificLearningArea: string }[];
  }[];
  lessons: {
    lessonsId: number;
    lessonNumber: number;
    learningObjectives: LearningObjective[];
  }[];
  successCriterias: { criteria: string }[];
  assessmentCriterias: {
    assessmentSuccessCriteriasId: number;
    yearNames: string;
    criteria: string;
  }[];
};

export type LessonPlanEquipment = {
  virtualPECupboardsId: number;
  equipmentsId: number;
  name: string;
  pupilsPerItem: string | null;
  bIsResourceCard: boolean;
  imagePath: string | null;
};

export type LessonPlanResource = {
  resourcesId: number;
  name: string;
  fileExtension: string | null;
  resourceType: string;
  bCanOpen: boolean;
  bCanDownload: boolean;
};

export type PresentationResource = {
  resourcesId: number;
  name: string;
  resourceType: string;
  videoURL: string | null;
  additionalAudioURL: string | null;
};

export type LessonPlan = {
  keyStage: LessonsKeyStage;
  keyStageName: string;
  schoolName: string;
  access: LessonsAccess;
  activity: LessonsActivity;
  lesson: {
    lessonsId: number;
    lessonNumber: number;
    theme: string | null;
    wholeChildObjectives: string | null;
    objectivesImagePath: string | null;
    warmUpAndIntroduction: string | null;
    skillDevelopment: string | null;
    creatingPerformingTheDance: string | null;
    plenary: string | null;
    learningObjectives: LearningObjective[];
  };
  presentationName: string | null;
  compulsoryEquipment: LessonPlanEquipment[];
  optionalEquipment: LessonPlanEquipment[];
  resources: LessonPlanResource[];
  presentationResources: PresentationResource[];
  successCriterias: {
    assessmentSuccessCriteriasId: number;
    criteria: string;
    applicableYearsNames: string | null;
  }[];
  previousLesson: { lessonsId: number; lessonNumber: number } | null;
  nextLesson: { lessonsId: number; lessonNumber: number } | null;
};

export type PrintQuota = {
  quotaValue: number;
  quotaLimit: number;
  bPrintQuotaExceeded: boolean;
  warningMessage: string | null;
};

export type LessonsQuery = {
  subjectsId: number;
  keyStage: string;
  activity: string;
  years: string;
};

/**
 * A call's details, 'notFound' when the API refuses the request (a 400: not found or no access),
 * or 'error' when it fails any other way
 */
export type LessonsResult<T> = T | 'notFound' | 'error';

const getDetails = async <T>(
  url: string,
  params: object
): Promise<LessonsResult<T>> => {
  try {
    const response = await instance.get<ApiResponse<T>>(url, { params });
    return response.data.status === '1' ? response.data.details : 'notFound';
  } catch (error: any) {
    return error?.response?.status === 400 ? 'notFound' : 'error';
  }
};

let subjectsCache: Promise<Subject[]> | null = null;

const getSubjects = () => {
  if (!subjectsCache) {
    subjectsCache = getDetails<{ subjects: Subject[] }>(
      endpoints.lessons.subjects,
      {}
    ).then((result) => {
      if (typeof result === 'string') {
        subjectsCache = null;
        throw new Error('Subjects could not be loaded');
      }
      return result.subjects;
    });
  }
  return subjectsCache;
};

export class LessonsApi {
  /**
   * Finds a live subject by the code in a page path, which is its InternalCode in lower case
   */
  public static getSubjectByCode = async (
    code: string
  ): Promise<LessonsResult<Subject>> => {
    try {
      const subjects = await getSubjects();
      return (
        subjects.find(
          (subject) => subject.internalCode.toLowerCase() === code.toLowerCase()
        ) ?? 'notFound'
      );
    } catch {
      return 'error';
    }
  };

  /**
   * The subject's labels (Activity, Equipment and so on), by content name
   */
  public static getSubjectContents = async (
    subjectsId: number
  ): Promise<SubjectContents> => {
    const result = await getDetails<{
      contents: { contentName: string; contentText: string }[];
    }>(endpoints.lessons.subjectContents, { subjectsId });

    return Object.fromEntries(
      (typeof result === 'string' ? [] : result.contents).map((content) => [
        content.contentName,
        content.contentText,
      ])
    );
  };

  public static getUnit = (query: LessonsQuery) =>
    getDetails<Unit>(endpoints.lessons.unit, query);

  public static getSchemeOfWork = (query: LessonsQuery) =>
    getDetails<SchemeOfWork>(endpoints.lessons.schemeOfWork, query);

  public static getLessonPlan = (query: LessonsQuery, lessonNumber: string) =>
    getDetails<LessonPlan>(endpoints.lessons.lessonPlan, {
      ...query,
      lessonNumber,
    });

  public static getPresentation = (query: LessonsQuery, lessonNumber: string) =>
    getDetails<LessonPlan>(endpoints.lessons.presentation, {
      ...query,
      lessonNumber,
    });

  /**
   * Counts a print against the school user's monthly limit, or null when the check fails
   */
  public static checkPrintQuota = async (
    subjectsId: number,
    printPageUrl: string
  ): Promise<PrintQuota | null> => {
    try {
      const response = await instance.post<ApiResponse<PrintQuota>>(
        endpoints.lessons.checkPrintQuota,
        { subjectsID: subjectsId, printPageUrl }
      );
      return response.data.status === '1' ? response.data.details : null;
    } catch {
      return null;
    }
  };

  public static getResourceFileUrl = async (
    subjectsId: number,
    resourcesId: number,
    bDownload: boolean
  ) => {
    const result = await getDetails<{ filePath: string }>(
      endpoints.lessons.resourceFile,
      { subjectsId, resourcesId, bDownload }
    );
    return typeof result === 'string' ? null : result.filePath;
  };

  public static getEquipmentFileUrl = async (
    subjectsId: number,
    equipmentsId: number,
    bDownload: boolean
  ) => {
    const result = await getDetails<{ filePath: string }>(
      endpoints.lessons.equipmentFile,
      { subjectsId, equipmentsId, bDownload }
    );
    return typeof result === 'string' ? null : result.filePath;
  };
}
